import { ChevronDownIcon, ChevronUpIcon, InfoIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button, DropdownMenu, Switch, Tooltip, TooltipProvider } from 'slab'
import { usePostPayrollManageAutomatedDeductions, usePostPayrollManageDisconnect } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { getBrowserTimezoneDatetimeString } from '../utils'
import { useFinchIntegration } from './hooks/useFinchIntegration'
import { useKomboIntegration } from './hooks/useKomboIntegration'
import { useMergeIntegration } from './hooks/useMergeIntegration'
import { createPayrollReauthenticationStrategies } from './payroll/payrollReauthenticationStrategies'
import PayrollDeductionBanner from './PayrollDeductionBanner'
import { ConfirmationDialog } from './reusable/ConfirmationDialog'

interface PayrollSyncStatusProps {
  isConnectedToPayroll: boolean | null
  lastEmployeeSync: Date | null
  nextEmployeeSync: Date | null
  payrollIntegrationProvider: string | null
  providerIconUrl: string | null
  providerApiConnectionMethod: string | null
  providerConfigId: string | null
  supportsAutomaticDeductions: boolean
  automaticDeductionsSetupComplete: boolean
  automaticDeductionsEnabled: boolean
  onDisconnectSuccess?: () => void
  requiresEmployeeDeduplication: boolean
  hasInvitedManuallyAddedEmployees: boolean
  showAutomatedDeductionsNotice?: boolean
  hidePayrollManagement?: boolean
}

const PayrollSyncStatus = ({
  isConnectedToPayroll,
  lastEmployeeSync,
  nextEmployeeSync,
  payrollIntegrationProvider,
  providerIconUrl,
  providerApiConnectionMethod,
  providerConfigId,
  supportsAutomaticDeductions,
  automaticDeductionsSetupComplete,
  automaticDeductionsEnabled,
  onDisconnectSuccess,
  requiresEmployeeDeduplication,
  hasInvitedManuallyAddedEmployees,
  showAutomatedDeductionsNotice,
  hidePayrollManagement = false,
}: PayrollSyncStatusProps) => {
  const { reload } = useWindowLocation()

  const [reauthenticatingProvider, setReauthenticatingProvider] = useState<string | null>(null)
  const [localAutomaticDeductionsEnabled, setLocalAutomaticDeductionsEnabled] = useState(automaticDeductionsEnabled)

  // Confirmation dialog states
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [showEnableDeductionsDialog, setShowEnableDeductionsDialog] = useState(false)
  const [showDisableDeductionsDialog, setShowDisableDeductionsDialog] = useState(false)

  // Initialize integration hooks
  const finchIntegration = useFinchIntegration({
    onSuccessCallback: () => {
      reload() // Stay on the same page, we'll reload after success
    }
  })

  const mergeIntegration = useMergeIntegration({
    onSuccessCallback: () => {
      reload() // Stay on the same page, we'll reload after success
    }
  })

  const komboIntegration = useKomboIntegration({
    onSuccessCallback: () => {
      reload() // Stay on the same page, we'll reload after success
    }
  })

  // Create reauthentication strategies
  const reauthStrategies = createPayrollReauthenticationStrategies(
    finchIntegration,
    mergeIntegration,
    komboIntegration,
    {
      onSuccess: () => {
        toast.success('Successfully reauthenticated with payroll provider')
        // Refresh the page to get updated data
        reload()
      },
      payrollProviderConfigId: providerConfigId || undefined,
    }
  )

  const disconnectMutation = usePostPayrollManageDisconnect({
    mutation: {
      onSuccess: () => {
        onDisconnectSuccess?.()
        toast.success('Successfully disconnected from payroll provider')
      },
      onError: () => {
        toast.error('Failed to disconnect from payroll provider')
      },
    },
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })

  const automatedDeductionsMutation = usePostPayrollManageAutomatedDeductions({
    mutation: {
      onSuccess: (_data, variables) => {
        const newEnabledState = variables.data.enabled
        setLocalAutomaticDeductionsEnabled(newEnabledState)

        toast.success(
          newEnabledState
            ? 'Successfully enabled automated deductions'
            : 'Successfully disabled automated deductions'
        )
      },
      onError: () => {
        toast.error('Failed to toggle automated deductions')
      },
    },
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })

  // Handle modal opening when reauthentication is ready - only for the specific provider
  useEffect(() => {
    if (reauthenticatingProvider && reauthenticatingProvider in reauthStrategies) {
      const strategy = reauthStrategies[reauthenticatingProvider as keyof typeof reauthStrategies]
      if (strategy.isModalReady()) {
        strategy.openModal()
        strategy.cleanup()
        setReauthenticatingProvider(null)
      }
    }
  }, [reauthStrategies, reauthenticatingProvider])

  const providerName = payrollIntegrationProvider || 'your payroll provider'

  const formatDate = (date: Date | null) => {
    if (!date) return null
    return getBrowserTimezoneDatetimeString(date)
  }

  const formattedLastSync = formatDate(lastEmployeeSync)
  const formattedNextSync = formatDate(nextEmployeeSync)

  // Determine sync status text
  let syncStatusText = 'Not synced yet'
  if (formattedLastSync) {
    syncStatusText = `Last synced on ${formattedLastSync}`
  } else if (formattedNextSync && isConnectedToPayroll) {
    syncStatusText = `First sync by ${formattedNextSync}`
  }

  const nextSyncInfo = (
    <div>
      Next sync by {formattedNextSync}
    </div>
  )

  const shouldShowTooltip = isConnectedToPayroll && lastEmployeeSync !== null

  const handleDisconnect = () => {
    setShowDisconnectDialog(true)
  }

  const confirmDisconnect = () => {
    disconnectMutation.mutate()
    setShowDisconnectDialog(false)
  }

  const handleReauthenticate = async () => {
    if (!providerApiConnectionMethod) {
      toast.error('Unable to determine provider type for reauthentication')
      return
    }

    if (!(providerApiConnectionMethod in reauthStrategies)) {
      toast.error('Reauthentication not supported for this provider')
      return
    }

    const strategy = reauthStrategies[providerApiConnectionMethod as keyof typeof reauthStrategies]

    try {
      setReauthenticatingProvider(providerApiConnectionMethod)
      await strategy.startReauthentication()
      // The useEffect will handle opening the modal when ready
    } catch {
      toast.error('Error starting reauthentication, please try again.')
      setReauthenticatingProvider(null)
    }
  }

  const handleAutomatedDeductionsToggle = () => {
    if (!localAutomaticDeductionsEnabled && !automaticDeductionsSetupComplete) {
      setShowEnableDeductionsDialog(true)
    } else if (localAutomaticDeductionsEnabled) {
      // If disabling, show confirmation
      setShowDisableDeductionsDialog(true)
    } else {
      // If enabling and setup is complete, show confirmation
      setShowEnableDeductionsDialog(true)
    }
  }

  const confirmEnableDeductions = () => {
    if (!automaticDeductionsSetupComplete) {
      // Start reauthentication flow for setup
      handleReauthenticate()
    } else {
      // Enable deductions
      automatedDeductionsMutation.mutate({
        data: {
          enabled: true,
        },
      })
    }
    setShowEnableDeductionsDialog(false)
  }

  const confirmDisableDeductions = () => {
    automatedDeductionsMutation.mutate({
      data: {
        enabled: false,
      },
    })
    setShowDisableDeductionsDialog(false)
  }

  return (
    <>
      {supportsAutomaticDeductions && (
        <PayrollDeductionBanner
          lastEmployeeSync={lastEmployeeSync}
          requiresEmployeeDeduplication={requiresEmployeeDeduplication}
          automaticDeductionsSetupComplete={automaticDeductionsSetupComplete}
          automaticDeductionsEnabled={localAutomaticDeductionsEnabled}
          hideSettingsCta
          showAutomatedDeductionsNotice={showAutomatedDeductionsNotice}
          payrollProviderName={providerName}
        />
      )}
      <div className='bg-white border border-gray-200 rounded-2xl shadow-sm mb-6' data-testid='payroll-sync-status'>
        <div className='flex flex-col p-6 gap-y-6'>
          <div className='flex flex-col md:flex-row md:justify-between gap-y-4 md:gap-x-6 md:items-center'>

            <div className='flex items-center gap-4'>
              {providerIconUrl && (
                <div className='w-10 h-10 rounded-full overflow-hidden flex-shrink-0'>
                  <img
                    src={providerIconUrl}
                    alt={`${providerName} logo`}
                    className='w-full h-full object-cover border rounded-full'
                  />
                </div>
              )}

              <div className='flex flex-col gap-y-2'>
                <div className='flex items-center'>
                  {isConnectedToPayroll
                    ? (
                      <>
                        <div className='w-2.5 h-2.5 rounded-full bg-green-400 mr-2 flex-shrink-0' />
                        <span className='body-bold text-gray-800'>
                          Synced from {providerName}
                        </span>
                      </>
                      )
                    : (
                      <>
                        <div className='w-2.5 h-2.5 rounded-full bg-red-400 mr-2 flex-shrink-0' />
                        <span className='body-bold text-gray-800'>
                          Not connected to {providerName}
                        </span>
                      </>
                      )}
                </div>

                <div className='flex items-center space-x-1'>
                  <span className='body-sm text-gray-800'>
                    {syncStatusText}
                  </span>
                  {shouldShowTooltip && (
                    <TooltipProvider>
                      <Tooltip content={nextSyncInfo}>
                        <InfoIcon aria-label='Next sync by' size={16} className='shrink-0' />
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>

            {/* Right side actions */}
            {!hidePayrollManagement && (
              <div className='flex items-center' data-testid='payroll-management-actions'>
                {providerApiConnectionMethod === 'adp' && isConnectedToPayroll
                  ? (
                    <TooltipProvider>
                      <Tooltip content='You must disconnect your payroll connection from the ADP Marketplace before you can disconnect from Thatch'>
                        <div>
                          <Button variant='secondary' disabled>
                            Manage
                          </Button>
                        </div>
                      </Tooltip>
                    </TooltipProvider>
                    )
                  : (
                    <DropdownMenu
                      trigger={({ open }) => (
                        <Button
                          variant='secondary'
                          rightIcon={open ? ChevronUpIcon : ChevronDownIcon}
                        >
                          Manage
                        </Button>
                      )}
                    >
                      {providerApiConnectionMethod !== 'adp' && (
                        <DropdownMenu.Item onSelect={handleReauthenticate}>
                          Reauthenticate
                        </DropdownMenu.Item>
                      )}
                      <DropdownMenu.Item
                        type='destructive'
                        onSelect={handleDisconnect}
                        disabled={disconnectMutation.isPending}
                      >
                        {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                      </DropdownMenu.Item>
                    </DropdownMenu>
                    )}
              </div>
            )}
          </div>

          {supportsAutomaticDeductions && (
            <div className='border-t border-gray-200 pt-4'>
              <div className='flex items-center justify-between gap-4'>
                <div>
                  <h4 className='body-bold text-gray-800'>Automated payroll deductions</h4>
                  <p className='body-sm text-gray-500 mt-1'>
                    {localAutomaticDeductionsEnabled ? "We're deducting" : 'We can deduct'} employee plan premium contributions via {providerName}.
                  </p>
                </div>
                <Switch
                  checked={localAutomaticDeductionsEnabled && Boolean(isConnectedToPayroll)}
                  onChange={handleAutomatedDeductionsToggle}
                  disabled={!isConnectedToPayroll || automatedDeductionsMutation.isPending || hasInvitedManuallyAddedEmployees}
                />
              </div>
            </div>
          )}
        </div>

        {/* Confirmation Dialogs */}
        <ConfirmationDialog
          open={showDisconnectDialog}
          onOpenChange={setShowDisconnectDialog}
          title={`Disconnect from ${providerName}`}
          submitLabel='Disconnect'
          isDanger
          isSubmitting={disconnectMutation.isPending}
          onSubmit={confirmDisconnect}
        >
          <div className='text-sm'>
            This will stop employee data syncs from {providerName}. You'll need to reconnect to resume syncing.
          </div>
        </ConfirmationDialog>

        <ConfirmationDialog
          open={showEnableDeductionsDialog}
          onOpenChange={setShowEnableDeductionsDialog}
          title='Enable automated payroll deductions'
          submitLabel='Enable'
          isSubmitting={automatedDeductionsMutation.isPending}
          onSubmit={confirmEnableDeductions}
        >
          <div className='text-sm'>
            {!automaticDeductionsSetupComplete
              ? `This will set up automated deductions with ${providerName}. You'll need to reauthenticate to enable this feature.`
              : `This will automatically deduct employee plan premium contributions via ${providerName}.`}
          </div>
        </ConfirmationDialog>

        <ConfirmationDialog
          open={showDisableDeductionsDialog}
          onOpenChange={setShowDisableDeductionsDialog}
          title='Disable automated payroll deductions'
          submitLabel='Disable'
          isDanger
          isSubmitting={automatedDeductionsMutation.isPending}
          onSubmit={confirmDisableDeductions}
        >
          <div className='text-sm'>
            This will stop automated management of employee plan premium deductions in {providerName}.
            You'll need to manually manage deductions.
            <br />
            <strong>Employees will not be unenrolled from existing deductions. </strong>
            If you would like to unenroll employees, you can do so manually in {providerName}.
          </div>
        </ConfirmationDialog>
      </div>
    </>
  )
}

export default PayrollSyncStatus
